import { useEffect, useRef, useState } from "react";

import { Loader, Select } from "@/components/ui/elements";

import { useGetBankBranches } from "@/hooks/bank";
import { YUCHO_BANK_CODE } from "@/util/yuchoBranchSearch";

const DEFAULT_BRANCH_DESCRIPTION =
  "支店名を入力すると該当する支店が表示されます。支店数が多い場合は具体的な名前を入力してください。";

const YUCHO_BRANCH_DESCRIPTION =
  "ゆうちょ銀行の支店名は「〇五八」のように漢数字で表示されます。通帳の店番（3桁の半角数字）でも検索できます。";

type Props = {
  bankcode: string;
  defaultbranch: string | undefined;
  defaultbranchName: string | undefined;
  branchReloadToken?: number;
  getBranchCode: (code: string) => void;
  getBranchName: (name: string) => void;
  disabled?: boolean;
};

type BankItem = {
  value: string;
  label: string;
};

export function BranchNameInput({
  bankcode,
  defaultbranch,
  defaultbranchName,
  branchReloadToken = 0,
  getBranchCode,
  getBranchName,
  disabled = false,
}: Props) {
  const [brancheList, setBrancheList] = useState<BankItem[]>([]);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    defaultbranch || null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(defaultbranchName || "");
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  const timeoutRef = useRef<number | null>(null);
  const fetchRequestIdRef = useRef(0);
  const { getBranches } = useGetBankBranches();

  // 支店を取得（検索クエリ付き）
  const fetchBranches = async (bankCode: string, query: string = "") => {
    const requestId = ++fetchRequestIdRef.current;
    setIsLoading(true);

    try {
      const result = await getBranches(bankCode, query);

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      if (!Array.isArray(result) || !result.length) {
        // 編集時は API 結果が空でも既存支店を表示し続ける
        if (defaultbranch && defaultbranchName) {
          setBrancheList([{ value: defaultbranch, label: defaultbranchName }]);
        } else {
          setBrancheList([]);
        }
        return;
      }

      const list = result.map((item) => ({
        value: item.code,
        label: item.name,
      }));

      // 編集時：取得結果に既存支店がなければ追加
      if (defaultbranch && defaultbranchName) {
        const existingBranch = list.find(
          (branch) => branch.value === defaultbranch,
        );
        if (!existingBranch) {
          list.push({ value: defaultbranch, label: defaultbranchName });
        }
      }

      setBrancheList(list);
    } catch (_error) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      if (defaultbranch && defaultbranchName) {
        setBrancheList([{ value: defaultbranch, label: defaultbranchName }]);
      } else {
        setBrancheList([]);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  // 支店一覧を取得（編集時は支店名で絞り込み、それ以外は最大100件）
  const loadBranchOptions = (bankCode: string, query: string = "") => {
    if (defaultbranch && defaultbranchName && query === "") {
      setSearchValue(defaultbranchName);
      setSelectedValue(defaultbranch);
      setBrancheList([{ value: defaultbranch, label: defaultbranchName }]);
    }
    fetchBranches(bankCode, query);
  };

  // 金融機関の選択・変更時に支店リストを取得
  useEffect(() => {
    if (!bankcode) {
      fetchRequestIdRef.current += 1;
      setBrancheList([]);
      setHasInitialLoad(false);
      return;
    }

    if (disabled) {
      return;
    }

    fetchRequestIdRef.current += 1;
    setHasInitialLoad(true);

    const initialQuery =
      defaultbranch && defaultbranchName ? defaultbranchName : "";

    if (!initialQuery) {
      setSearchValue("");
      setSelectedValue(null);
      setBrancheList([]);
    }

    loadBranchOptions(bankcode, initialQuery);
  }, [bankcode, disabled, branchReloadToken]);

  // 支店をクリアしたときは一覧（最大100件）を再取得
  useEffect(() => {
    if (!bankcode || disabled || !hasInitialLoad) return;
    if (defaultbranch || defaultbranchName) return;
    if (searchValue.trim() !== "") return;

    fetchBranches(bankcode, "");
  }, [defaultbranch, defaultbranchName, bankcode, disabled, hasInitialLoad]);

  // 検索値が変わったときにAPI呼び出し（debounce付き）
  useEffect(() => {
    if (!hasInitialLoad || !bankcode || disabled) return;

    if (searchValue.trim() === "") {
      return;
    }

    // 既存のタイマーをクリア
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 300ms後にAPI呼び出し
    timeoutRef.current = window.setTimeout(() => {
      fetchBranches(bankcode, searchValue);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchValue, bankcode, disabled, hasInitialLoad]);

  // 銀行コードが変わったときに選択値をリセット
  useEffect(() => {
    // 新しい銀行コードが設定され、かつ現在の選択値が前の銀行の支店の場合はリセット
    if (bankcode && selectedValue && selectedValue !== defaultbranch) {
      setSelectedValue(null);
    }
  }, [bankcode]);

  // defaultbranchの変更を監視（編集時の状態同期）
  useEffect(() => {
    // defaultbranchが空になった場合は選択値もクリア
    if (!defaultbranch && selectedValue) {
      setSelectedValue(null);
    }
    // defaultbranchが設定され、現在の選択値と異なる場合は同期
    else if (defaultbranch && selectedValue !== defaultbranch) {
      setSelectedValue(defaultbranch);
    }
  }, [defaultbranch]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleChange = (value: string | null) => {
    setSelectedValue(value);
    if (value) {
      getBranchCode(value);
      // 選択された支店の名前を取得
      const selectedBranch = brancheList.find(
        (branch) => branch.value === value,
      );
      if (selectedBranch) {
        getBranchName(selectedBranch.label);
        setSearchValue(selectedBranch.label);
      }
    } else {
      // 値が削除された場合は空文字列で親に通知
      getBranchCode("");
      getBranchName("");
      setSearchValue("");
      if (bankcode) {
        fetchBranches(bankcode, "");
      }
    }
  };

  const handleBlur = () => {
    // 候補未選択の blur ではフォーム値をセットしない（handleChange のみ）
    // Mantine が選択済みでも blur で表示を空にすることがあるため、API 名称を復元する
    if (selectedValue) {
      const selectedBranch = brancheList.find(
        (branch) => branch.value === selectedValue,
      );
      if (selectedBranch) {
        getBranchName(selectedBranch.label);
        setSearchValue(selectedBranch.label);
      }
    }
  };

  const handleDropdownOpen = () => {
    if (!bankcode || disabled || isLoading) return;
    if (searchValue.trim() !== "") return;
    if (defaultbranch && defaultbranchName) return;
    if (brancheList.length > 1) return;

    fetchBranches(bankcode, "");
  };

  return (
    <Select
      value={selectedValue}
      withAsterisk
      data={brancheList}
      searchable
      clearable={!isLoading}
      onSearchChange={handleSearchChange}
      searchValue={searchValue}
      // API側で検索済みのため、入力文字とラベルの不一致（058 vs 〇五八）で候補が消えないようにする
      filter={({ options }) => options}
      placeholder={
        bankcode ? "支店名を入力してください" : "まず金融機関を選択してください"
      }
      description={
        bankcode === YUCHO_BANK_CODE
          ? YUCHO_BRANCH_DESCRIPTION
          : DEFAULT_BRANCH_DESCRIPTION
      }
      maw={200}
      label="支店名"
      nothingFoundMessage={
        isLoading ? (
          <Loader />
        ) : searchValue.length > 0 ? (
          <div style={{ padding: "8px", fontSize: "12px", color: "#666" }}>
            該当する支店が見つかりません。
            <br />
            正確な支店名を入力してください。
          </div>
        ) : (
          <div style={{ padding: "8px", fontSize: "12px", color: "#666" }}>
            支店名を入力してください。
            <br />
            大手銀行は支店数が多いため、
            <br />
            3文字以上の入力を推奨します。
          </div>
        )
      }
      limit={50}
      onChange={handleChange}
      onBlur={handleBlur}
      onDropdownOpen={handleDropdownOpen}
      rightSection={isLoading ? <Loader size="xs" /> : undefined}
      disabled={disabled || !bankcode}
    />
  );
}
